<div class="container">
  <p class="warning-message" i18n="@@cameraAccessDenied">
    It looks like camera access was permanently denied for this website. To
    enable it, please follow these steps for your device/browser:
  </p>

  <div class="instructions-section" *ngIf="selectedInstructionsType">
    <ng-container [ngSwitch]="selectedInstructionsType">
      <div *ngSwitchCase="'mobile'">
        <ng-container [ngSwitch]="detectMobileBrowser()">
          <div *ngSwitchCase="'safari-ios'">
            <h4 i18n="@@safariIOSHeading">For Safari on iOS (iPhone/iPad):</h4>
            <p i18n="@@safariIOSPermissionsNote">
              Permissions for websites on iOS are controlled through the device's
              <strong>Settings</strong> app.
            </p>
            <ol>
              <li i18n="@@safariIOSStep1">
                Go to your device's <strong>Settings</strong> app (the grey gear
                icon).
              </li>
              <li i18n="@@safariIOSStep2">
                Scroll down and tap <strong>Safari</strong>.
              </li>
              <li i18n="@@safariIOSStep3">
                Scroll down to <strong>Settings for Websites</strong> (or
                <strong>Website Settings</strong>).
              </li>
              <li i18n="@@safariIOSStep4">Tap <strong>Camera</strong>.</li>
              <li i18n="@@safariIOSStep5">
                Find our website's URL (<ph id="0" equiv-text="{{ currentSiteUrl }}"/>) and set its
                permission to <strong>Ask</strong> or <strong>Allow</strong>.
              </li>
              <li i18n="@@safariIOSStep6">
                <strong>Important:</strong> After changing, close this tab, reopen
                it, or refresh the page.
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://support.apple.com/guide/iphone/iph168c4bbd5/ios"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@safariIOSOfficialLinkText"
              >
                View Official Safari for iOS Help
              </a>
              <span i18n="@@safariIOSLinkNote"
              >(search for "Camera" or "microphone" on the page)</span
              >
            </p>
          </div>

          <div *ngSwitchCase="'chrome-android'">
            <h4 i18n="@@chromeAndroidHeading">For Chrome on Android:</h4>
            <ol>
              <li i18n="@@chromeAndroidStep1">
                <strong>Tap the address bar (where the website URL is)</strong> at the top of your screen. A small pop-up should appear, often showing "Permissions: Camera Blocked" or a similar message.
              </li>
              <li i18n="@@chromeAndroidStep2">
                In this pop-up, tap the <strong>Camera</strong> option or a
                <strong>Settings icon</strong> next to the permission. Change the
                setting to <strong>Allow</strong>.
              </li>
              <li i18n="@@chromeAndroidStep3">
                If the above doesn't work or you can't find the option, proceed
                with the steps below via Device Settings.
              </li>
              <li i18n="@@chromeAndroidStep4">
                Go to your device's main <strong>Android Settings</strong> app (the
                grey gear icon on your phone's home screen or app drawer).
              </li>
              <li i18n="@@chromeAndroidStep5">
                Tap <strong>Apps &amp; notifications</strong> (or
                <strong>Apps</strong>, <strong>Applications</strong>,
                <strong>App management</strong>).
              </li>
              <li i18n="@@chromeAndroidStep6">
                Find and tap on <strong>Chrome</strong> in the list of apps.
              </li>
              <li i18n="@@chromeAndroidStep7">Tap <strong>Permissions</strong>.</li>
              <li i18n="@@chromeAndroidStep8">
                Find <strong>Camera</strong> and set the permission to
                <strong>Allow</strong> (or "Ask every time," "Only allow while using
                the app").
              </li>
              <li i18n="@@chromeAndroidStep9">
                <strong>Important:</strong> After changing, force-close the browser
                app completely and then reopen it, or restart your phone.
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://support.google.com/chrome/answer/114662?hl=en&co=GENIE.Platform%3DAndroid"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@chromeAndroidOfficialLinkText"
              >
                View Official Chrome for Android Help
              </a>
              <span i18n="@@chromeAndroidLinkNote"
              >(look for "Change a site's camera and microphone permissions")</span
              >
            </p>
          </div>

          <div *ngSwitchCase="'firefox-mobile'">
            <h4 i18n="@@firefoxMobileHeading">For Firefox on Mobile (Android/iOS):</h4>
            <ol>
              <li i18n="@@firefoxMobileStep1">
                Open Firefox. Tap the <strong>three dots</strong> (⋮) or
                <strong>menu icon</strong> (☰) in the corner.
              </li>
              <li i18n="@@firefoxMobileStep2">Tap <strong>Settings</strong>.</li>
              <li i18n="@@firefoxMobileStep3">
                Tap <strong>Site Permissions</strong> (or <strong>Permissions</strong>).
              </li>
              <li i18n="@@firefoxMobileStep4">Tap <strong>Camera</strong>.</li>
              <li i18n="@@firefoxMobileStep5">
                Find our website's URL (<ph id="0" equiv-text="{{ currentSiteUrl }}"/>) and set its
                permission to <strong>Allow</strong> or <strong>Always Ask</strong>.
              </li>
              <li i18n="@@firefoxMobileStep6">
                <strong>Important:</strong> After changing, close this tab, reopen
                it, or refresh the page.
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://support.mozilla.org/en-US/kb/permissions-firefox-android"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@firefoxMobileOfficialLinkText"
              >
                View Official Firefox Mobile Help
              </a>
              <span i18n="@@firefoxMobileLinkNote"
              >(look for "Change camera permissions")</span
              >
            </p>
          </div>

          <div *ngSwitchCase="'samsung-internet'">
            <h4 i18n="@@samsungInternetHeading">For Samsung Internet Browser:</h4>
            <ol>
              <li i18n="@@samsungInternetStep1">
                Open Samsung Internet. Tap the <strong>three lines</strong> (☰) or
                <strong>menu icon</strong> in the bottom right corner.
              </li>
              <li i18n="@@samsungInternetStep2">Tap <strong>Settings</strong>.</li>
              <li i18n="@@samsungInternetStep3">Tap <strong>Sites and downloads</strong>.</li>
              <li i18n="@@samsungInternetStep4">Tap <strong>Site permissions</strong>.</li>
              <li i18n="@@samsungInternetStep5">Tap <strong>Camera</strong>.</li>
              <li i18n="@@samsungInternetStep6">
                Find our website's URL (<ph id="0" equiv-text="{{ currentSiteUrl }}"/>) and set its
                permission to <strong>Allow</strong> or <strong>Ask</strong>.
              </li>
              <li i18n="@@samsungInternetStep7">
                <strong>Important:</strong> After changing, close this tab, reopen
                it, or refresh the page.
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://eu.community.samsung.com/t5/mobile-apps-services/allowing-a-website-permission-to-access-the-camera/td-p/5501468"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@samsungInternetOfficialLinkText"
              >
                View Samsung Internet Help
              </a>
              <span i18n="@@samsungInternetLinkNote"
              >(General browser settings, search for "Camera permissions")</span
              >
            </p>
          </div>

          <div *ngSwitchCase="'other-mobile'">
            <p i18n="@@otherMobileInstructions">
              Please refer to the general instructions for your mobile browser:
            </p>
            <ol>
              <li i18n="@@otherMobileStep1">
                Open your browser's <strong>Settings</strong> or
                <strong>Preferences</strong>.
              </li>
              <li i18n="@@otherMobileStep2">
                Look for <strong>Site Settings</strong>,
                <strong>Permissions</strong>, or
                <strong>Privacy and Security</strong>.
              </li>
              <li i18n="@@otherMobileStep3">
                Find <strong>Camera</strong> and ensure it's set to
                <strong>Allow</strong> or <strong>Ask</strong>.
              </li>
              <li i18n="@@otherMobileStep4">
                <strong>Important:</strong> After changing, close this tab, reopen
                it, or refresh the page.
              </li>
            </ol>
          </div>
        </ng-container>
      </div>

      <div *ngSwitchCase="'desktop'">
        <h4 i18n="@@desktopBrowserHeading">For your desktop browser:</h4>
        <p i18n="@@desktopBrowserInstructions">
          Please follow the steps below to enable camera access. After enabling,
          please refresh this page.
        </p>
        <ng-container [ngSwitch]="detectDesktopBrowser()">
          <div *ngSwitchCase="'chrome'">
            <ol>
              <li i18n="@@chromeDesktopStep1">
                <strong>Click the Camera icon or Lock icon </strong> in the
                address bar (usually on the right or left side) of your browser. A
                small menu will appear.
              </li>
              <li i18n="@@chromeDesktopStep2">
                Find the "Camera" permission for this site and change it to
                <strong>Allow</strong> or <strong>Always allow on this site</strong>.
              </li>
              <li i18n="@@chromeDesktopStep3">
                If the above doesn't work or you can't find the option, proceed
                with the steps below.
              </li>
              <li i18n="@@chromeDesktopStep4">
                Open a new tab and type
                <code>chrome://settings/content/camera</code> into the address bar,
                then press Enter.
              </li>
              <li i18n="@@chromeDesktopStep5">
                Find "{{ currentSiteUrl }}" in the list under "Block" (or "Not
                allowed to use your camera").
              </li>
              <li i18n="@@chromeDesktopStep6">
                Click on the site's name and change the "Camera" permission to
                "Allow".
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@chromeDesktopOfficialLinkText"
              >
                View Official Chrome for Desktop Help
              </a>
              <span i18n="@@chromeDesktopLinkNote"
              >(look for "Change a site's camera and microphone access")</span
              >
            </p>
          </div>
          <div *ngSwitchCase="'firefox'">
            <ol>
              <li i18n="@@firefoxDesktopStep1">
                Click the padlock icon or the "i" icon
                in the address bar next to our website's URL. A menu will appear.
              </li>
              <li i18n="@@firefoxDesktopStep2">
                Look for "Permissions" or "More information" and then locate the
                <strong>Camera</strong> setting for this site. Change it to
                <strong>Allow</strong> or <strong>Always Ask</strong>.
              </li>
              <li i18n="@@firefoxDesktopStep3">
                If the above doesn't work or you can't find the option, proceed
                with the steps below.
              </li>
              <li i18n="@@firefoxDesktopStep4">
                Open a new tab and type <code>about:preferences#privacy</code> into
                the address bar, then press Enter.
              </li>
              <li i18n="@@firefoxDesktopStep5">
                Scroll down to the "Permissions" section.
              </li>
              <li i18n="@@firefoxDesktopStep6">
                Next to "Camera", click "Settings...".
              </li>
              <li i18n="@@firefoxDesktopStep7">
                Find "<ph id="0" equiv-text="{{ currentSiteUrl }}"/>" in the list and change its
                permission to "Allow" or "Always Ask".
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@firefoxDesktopOfficialLinkText"
              >
                View Official Firefox Help
              </a>
              <span i18n="@@firefoxDesktopLinkNote"
              >(search for "Change camera permissions")</span
              >
            </p>
          </div>
          <div *ngSwitchCase="'safari'">
            <ol>
              <li i18n="@@safariDesktopStep1">
                Go to <strong>Safari</strong> in your Mac's top menu bar, then
                select <strong>Settings</strong> (or <strong>Preferences</strong>).
              </li>
              <li i18n="@@safariDesktopStep2">
                Click on the <strong>Websites</strong> tab.
              </li>
              <li i18n="@@safariDesktopStep3">
                In the sidebar, select <strong>Camera</strong>.
              </li>
              <li i18n="@@safariDesktopStep4">
                Find "<ph id="0" equiv-text="{{ currentSiteUrl }}"/>" in the list and set its permission
                to "Allow" or "Ask".
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@safariDesktopOfficialLinkText"
              >
                View Official Safari for Mac Help
              </a>
              <span i18n="@@safariDesktopLinkNote"
              >(search for "Control access to the camera on Mac")</span
              >
            </p>
          </div>
          <div *ngSwitchCase="'edge'">
            <ol>
              <li i18n="@@edgeDesktopStep1">
                <strong>Click the padlock icon</strong> in the address bar
                (usually on the left side) of your browser. A small menu will appear.
              </li>
              <li i18n="@@edgeDesktopStep2">
                Find "Permissions for this site" or "Site permissions" and then
                locate the <strong>Camera</strong> setting. Change it to
                <strong>Allow</strong>.
              </li>
              <li i18n="@@edgeDesktopStep3">
                If the above doesn't work or you can't find the option, proceed
                with the steps below.
              </li>
              <li i18n="@@edgeDesktopStep4">
                Open a new tab and type
                <code>edge://settings/content/camera</code> into the address bar,
                then press Enter.
              </li>
              <li i18n="@@edgeDesktopStep5">
                Find <ph id="0" equiv-text="{{ currentSiteUrl }}"/> in the list under "Block" (or "Not
                allowed to use your camera").
              </li>
              <li i18n="@@edgeDesktopStep6">
                Click on the site's name and change the "Camera" permission to
                "Allow".
              </li>
            </ol>
            <p class="official-link-note">
              <a
                href="https://support.microsoft.com/en-us/microsoft-edge/enable-camera-and-microphone-for-microsoft-edge-3c616805-4f4a-4136-836e-57e034448e89"
                target="_blank"
                rel="noopener noreferrer"
                i18n="@@edgeDesktopOfficialLinkText"
              >
                View Official Edge Help
              </a>
              <span i18n="@@edgeDesktopLinkNote">(search for "Camera")</span>
            </p>
          </div>
          <div *ngSwitchCase="'other'">
            <p i18n="@@otherDesktopInstructions">
              Please consult your browser's help documentation for instructions
              on how to change camera permissions for specific websites. Look
              for "Site Settings," "Privacy and Security," or "Permissions."
            </p>
          </div>
        </ng-container>
      </div>
    </ng-container>
    <p class="refresh-note" i18n="@@refreshNote">
      <strong>After making changes, please close and reopen this browser tab or
        app, or refresh the page, for the settings to take effect.</strong>
    </p>
  </div>
</div>
